import { api } from './api.js';
import type {
  UIToken,
  UITokenMode,
  UITokenPreset,
  UITokenPresetData,
  UIBuildLogEntry,
  UISettings,
  UIComponentFile,
} from '@elmes/shared';

export interface ApiOk<T> {
  success: boolean;
  data: T;
}

export const uiSystemService = {
  async getTokens(mode?: UITokenMode | 'all') {
    const res = await api.get<ApiOk<UIToken[]>>('/ui-system/tokens', {
      params: mode ? { mode } : {},
    });
    return res.data.data;
  },

  async updateTokens(tokens: Array<{ key: string; mode?: UITokenMode; value: string }>) {
    const res = await api.put<ApiOk<{ updated: number; keys: string[] }>>('/ui-system/tokens', {
      tokens,
    });
    return res.data.data;
  },

  async resetTokens(payload: { mode?: UITokenMode; keys?: string[] }) {
    const res = await api.post<ApiOk<{ reset: number }>>('/ui-system/tokens/reset', payload);
    return res.data.data;
  },

  async updateDefaults(
    tokens: Array<{ key: string; mode?: UITokenMode; defaultValue: string }>,
  ) {
    const res = await api.put<ApiOk<{ updated: number }>>('/ui-system/tokens/defaults', {
      tokens,
    });
    return res.data.data;
  },

  async exportTokens(): Promise<UITokenPresetData & { exportedAt: string }> {
    const res = await api.get('/ui-system/tokens/export', { responseType: 'text' });
    return typeof res.data === 'string' ? JSON.parse(res.data) : res.data;
  },

  async importTokens(payload: UITokenPresetData) {
    const res = await api.post<ApiOk<{ updated: number }>>('/ui-system/tokens/import', payload);
    return res.data.data;
  },

  async listPresets() {
    const res = await api.get<ApiOk<UITokenPreset[]>>('/ui-system/tokens/presets');
    return res.data.data;
  },

  async getPreset(id: number) {
    const res = await api.get<ApiOk<UITokenPreset>>(`/ui-system/tokens/presets/${id}`);
    return res.data.data;
  },

  async createPreset(input: { name: string; description?: string; fromCurrent?: boolean; data?: UITokenPresetData }) {
    const res = await api.post<ApiOk<{ id: number }>>('/ui-system/tokens/presets', input);
    return res.data.data;
  },

  async applyPreset(id: number) {
    const res = await api.post<ApiOk<{ updated: number }>>(`/ui-system/tokens/presets/${id}/apply`);
    return res.data.data;
  },

  async deletePreset(id: number) {
    const res = await api.delete<ApiOk<{ deleted: boolean }>>(`/ui-system/tokens/presets/${id}`);
    return res.data.data;
  },

  async listComponents() {
    const res = await api.get<ApiOk<UIComponentFile[]>>('/ui-system/components');
    return res.data.data;
  },

  async readComponent(path: string) {
    const res = await api.get<ApiOk<{ path: string; content: string }>>(
      '/ui-system/components/content',
      { params: { path } },
    );
    return res.data.data;
  },

  async readBackup(path: string) {
    const res = await api.get<ApiOk<{ path: string; content: string }>>(
      '/ui-system/components/backup',
      { params: { path } },
    );
    return res.data.data;
  },

  async writeComponent(
    path: string,
    content: string,
    opts?: { startLine?: number; endLine?: number },
  ) {
    const res = await api.put<ApiOk<{ path: string; buildTriggered: boolean }>>(
      '/ui-system/components/content',
      { path, content, ...opts },
    );
    return res.data.data;
  },

  async triggerBuild() {
    const res = await api.post<ApiOk<{ id: number; status: 'running' }>>('/ui-system/build/trigger');
    return res.data.data;
  },

  async getBuildStatus() {
    const res = await api.get<ApiOk<UIBuildLogEntry | null>>('/ui-system/build/status');
    return res.data.data;
  },

  async getBuildLogs(limit = 10) {
    const res = await api.get<ApiOk<UIBuildLogEntry[]>>('/ui-system/build/logs', {
      params: { limit },
    });
    return res.data.data;
  },

  async getSettings() {
    const res = await api.get<ApiOk<UISettings>>('/ui-system/settings');
    return res.data.data;
  },

  async updateSettings(data: Partial<UISettings>) {
    const res = await api.put<ApiOk<UISettings>>('/ui-system/settings', data);
    return res.data.data;
  },
};

/**
 * Inject tokens as CSS custom properties on :root.
 * For light mode tokens they go on `:root`; dark mode values are injected under `.dark` via a style tag.
 */
export function applyTokensToDOM(tokens: UIToken[]) {
  const root = document.documentElement;
  const light = tokens.filter((t) => t.mode === 'light');
  const dark = tokens.filter((t) => t.mode === 'dark');

  for (const token of light) {
    root.style.setProperty(token.key, token.value);
  }

  // Dark mode values via a managed <style> tag so `.dark` selector picks them up
  let styleEl = document.getElementById('elmes-dark-tokens') as HTMLStyleElement | null;
  if (!styleEl) {
    styleEl = document.createElement('style');
    styleEl.id = 'elmes-dark-tokens';
    document.head.appendChild(styleEl);
  }
  if (dark.length > 0) {
    styleEl.textContent = `.dark {\n${dark.map((t) => `  ${t.key}: ${t.value};`).join('\n')}\n}`;
  } else {
    styleEl.textContent = '';
  }
}

export async function loadAndApplyTokens() {
  try {
    const tokens = await uiSystemService.getTokens('all');
    applyTokensToDOM(tokens);
  } catch {
    // Unauthenticated or API unreachable — use CSS defaults from index.css
  }
}
